import type {
  TargetingBlockingRulesSchema,
  UseTargetingBlockingRulesSchemaFetch,
} from '@openx/types/targeting/targetingProviderConfig';
import { isObjectRecord } from '@openx/utils/lib/typeGuards/isObjectRecord';
import { useMemo } from 'react';
import { useOptionsByPathQuery } from '../../../hooks/useOptionsByPathQuery';

export const TARGETING_BLOCKING_RULES_SCHEMA_PATH = 'deal.package.targeting.block_irrelevant_targeting_params_schema';

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const extractSchema = (value: unknown): TargetingBlockingRulesSchema | null => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) {
    const firstSchema = parsedValue.find(isObjectRecord);

    return firstSchema ?? null;
  }

  if (isObjectRecord(parsedValue)) {
    return parsedValue;
  }

  return null;
};

export const useTargetingBlockingRulesSchemaFetch: UseTargetingBlockingRulesSchemaFetch = ({ enabled = true } = {}) => {
  const {
    data: { optionsByPath = [] } = {},
    loading,
    refetch,
  } = useOptionsByPathQuery({
    variables: {
      path: TARGETING_BLOCKING_RULES_SCHEMA_PATH,
    },
    skip: !enabled,
  });

  const targetingBlockingRulesSchema = useMemo(() => {
    const schemaOption = optionsByPath.find(Boolean);

    return extractSchema(schemaOption?.extra);
  }, [optionsByPath]);

  return {
    targetingBlockingRulesSchema,
    isTargetingBlockingRulesSchemaLoading: loading,
    refetchTargetingBlockingRulesSchema: refetch,
  };
};
